<template>
  <div class="password-update-dialog">
    <!-- 找回密码表单 -->
    <el-form
        ref="forgotFormRef"
        :model="forgotForm"
        :rules="forgotRules"
        class="password-form"
    >
      <el-form-item prop="phone">
        <el-input
            v-model="forgotForm.phone"
            placeholder="请输入手机号"
            size="large"
            class="form-input"
        >
          <template #prefix>
            <el-icon>
              <User/>
            </el-icon>
          </template>
        </el-input>
      </el-form-item>

      <el-form-item prop="verifyCode">
        <el-input
            v-model="forgotForm.verifyCode"
            placeholder="请输入验证码"
            size="large"
            class="form-input"
        >
          <template #prefix>
            <el-icon> <Message /></el-icon>
          </template>
          <template #suffix>
            <el-button
                link
                class="captcha-btn"
                :disabled="countdown > 0"
                @click="sendVerifyCode"
            >
              {{ countdown > 0 ? `${countdown}秒后重试` : '获取验证码' }}
            </el-button>
          </template>
        </el-input>
      </el-form-item>

      <el-form-item prop="newPassword">
        <el-input
            v-model="forgotForm.newPassword"
            type="password"
            placeholder="请输入新密码（至少8位）"
            size="large"
            class="form-input"
            show-password
        >
          <template #prefix>
            <el-icon>
              <Lock/>
            </el-icon>
          </template>
        </el-input>
      </el-form-item>

      <el-form-item prop="confirmPassword">
        <el-input
            v-model="forgotForm.confirmPassword"
            type="password"
            placeholder="请确认新密码"
            size="large"
            class="form-input"
            show-password
        >
          <template #prefix>
            <el-icon>
              <Lock/>
            </el-icon>
          </template>
        </el-input>
      </el-form-item>

      <div class="dialog-actions">
        <el-button
            @click="handleCancel"
            class="cancel-btn"
            size="large"
        >
          取消
        </el-button>
        <el-button
            type="primary"
            @click="handleForgotPassword"
            :loading="submitting"
            class="submit-btn"
            size="large"
        >
          重置密码
        </el-button>
      </div>
    </el-form>
  </div>
</template>

<script>
import { ref, reactive, onMounted, onUnmounted } from 'vue'
import { ElMessage } from 'element-plus'
import request from '@/utils/request'

export default {
  name: "PasswordUpdateDialog",
  emits: ['success', 'cancel'],

  setup(props, { emit }) {
    // 响应式数据
    const countdown = ref(0)
    const timer = ref(null)

    // 忘记密码表单
    const forgotForm = reactive({
      phone: '',
      verifyCode: '',
      newPassword: '',
      confirmPassword: ''
    })

    // 表单引用
    const forgotFormRef = ref()

    const submitting = ref(false)

    // 表单规则
    const forgotRules = {
      phone: [
        { required: true, message: '请输入手机号', trigger: ['blur', 'change'] },
        { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号', trigger: ['blur', 'change'] }
      ],
      verifyCode: [
        { required: true, message: '请输入验证码', trigger: ['blur', 'change'] },
        { pattern: /^\d{6}$/, message: '验证码应为6位数字', trigger: ['blur', 'change'] }
      ],
      newPassword: [
        { required: true, message: '请输入新密码', trigger: ['blur', 'change'] },
        { min: 8, message: '密码长度至少8位', trigger: ['blur', 'change'] },
        {
          pattern: /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}$/,
          message: '密码至少包含字母和数字',
          trigger: ['blur', 'change']
        }
      ],
      confirmPassword: [
        { required: true, message: '请确认新密码', trigger: ['blur', 'change'] },
        {
          validator: (rule, value, callback) => {
            if (value !== forgotForm.newPassword) {
              callback(new Error('两次输入的密码不一致'))
            } else {
              callback()
            }
          },
          trigger: ['blur', 'change']
        }
      ]
    }

    // 发送验证码
    const sendVerifyCode = async () => {
      if (!forgotFormRef.value) return

      // 1. 校验手机号字段
      try {
        await forgotFormRef.value.validateField('phone')
      } catch {
        // 校验失败，表单已显示错误，不发送请求
        return
      }

      // 2. 校验通过，发送验证码
      try {
        await request.get('/auth/verify-code/phone', {
          params: {
            phone: forgotForm.phone,
            scene: 'Bind'
          }
        })
        ElMessage.success('验证码发送成功')
        startCountdown()
      } catch (error) {
        ElMessage.error(error.response?.data?.message || '验证码发送失败')
      }
    }

    // 开始倒计时
    const startCountdown = () => {
      countdown.value = 60
      timer.value = setInterval(() => {
        countdown.value--
        if (countdown.value <= 0) {
          clearInterval(timer.value)
        }
      }, 1000)
    }

    // 忘记密码提交
    const handleForgotPassword = async () => {
      if (!forgotFormRef.value || submitting.value) return

      // 1. 校验表单
      try {
        await forgotFormRef.value.validate()
      } catch {
        // 校验失败，表单自动显示错误，直接返回，不弹任何 Toast
        return
      }

      // 2. 提交数据
      submitting.value = true
      try {
        const { confirmPassword, ...submitData } = forgotForm
        await request.post('/auth/forgot-password', submitData)
        emit('success')
      } catch (error) {
        ElMessage.error(error.response?.data?.message || '密码重置失败，请稍后重试')
      } finally {
        submitting.value = false
      }
    }

    // 取消操作
    const handleCancel = () => {
      resetForm()          // 先重置表单和校验状态
      emit('cancel')
    }

    // 清理定时器
    const clearTimer = () => {
      if (timer.value) {
        clearInterval(timer.value)
      }
    }

    const resetForm = () => {
      if (forgotFormRef.value) {
        forgotFormRef.value.resetFields()
      }
      Object.assign(forgotForm, {
        phone: '',
        verifyCode: '',
        newPassword: '',
        confirmPassword: ''
      })
      clearTimer()
      countdown.value = 0
    }

    onUnmounted(() => {
      clearTimer()
    })

    return {
      countdown,
      forgotForm,
      forgotFormRef,
      forgotRules,
      submitting,

      resetForm,
      sendVerifyCode,
      handleForgotPassword,
      handleCancel,
      clearTimer
    }
  }
}
</script>

<style scoped lang="scss">
.password-update-dialog {
  .password-form {
    min-height: 220px;
    margin: 0 !important;
    flex-direction: column;
    justify-content: flex-start;
  }

  /* 输入框样式 - 2026 现代圆角 + 蓝色 focus 光晕 */
  .form-input {
    :deep(.el-input__wrapper) {
      border-radius: 12px;
      padding: 1px 14px;
      box-shadow: 0 0 0 1px rgba(226, 232, 240, 0.9) inset;
      height: 46px;
      transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
      background: rgba(248, 250, 252, 0.4);

      &:hover {
        box-shadow: 0 0 0 1px #cbd5e1 inset;
        background: #fff;
      }

      &.is-focus {
        box-shadow: 0 0 0 1px #3b82f6 inset, 0 0 0 4px rgba(59, 130, 246, 0.12) !important;
        background: #fff;
      }
    }

    :deep(.el-input__prefix) {
      left: 12px;
      color: #3b82f6;

      .el-icon {
        font-size: 18px;
      }
    }

    :deep(.el-input__suffix) {
      display: flex;
      align-items: center;
      padding-right: 10px;
    }

    :deep(.el-input__inner) {
      padding-right: 90px;
      color: #1e293b;
    }

    .captcha-btn {
      padding: 0;
      height: auto;
      color: #3b82f6;
      font-size: 0.82rem;
      font-weight: 600;
      white-space: nowrap;
      margin-left: 4px;
      transition: color 0.25s ease;

      &:hover {
        color: #f97316;
      }

      &:disabled {
        color: #94a3b8;
        cursor: not-allowed;
      }
    }

    :deep(input) {
      font-size: 14px;
      height: 16px;
      line-height: 36px;

      &:-webkit-autofill,
      &:-webkit-autofill:hover,
      &:-webkit-autofill:focus,
      &:-webkit-autofill:active {
        -webkit-text-fill-color: #1e293b !important;
        -webkit-box-shadow: 0 0 0px 1000px white inset !important;
        transition: background-color 5000s ease-in-out 0s;
      }
    }
  }

  .dialog-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 24px;
  }

  .cancel-btn {
    width: 110px;
    border-radius: 12px;
    border: 1px solid rgba(226, 232, 240, 0.9);
    color: #475569;
    font-weight: 500;
    transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);

    &:hover {
      border-color: rgba(59, 130, 246, 0.3);
      color: #2563eb;
      background: rgba(59, 130, 246, 0.04);
    }
  }

  .submit-btn {
    width: 110px;
    border-radius: 12px;
    padding: 10px 16px;
    font-weight: 600;
    font-size: 0.9rem;
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    border: none;
    box-shadow: 0 6px 16px rgba(59, 130, 246, 0.3);
    height: 42px;
    transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 8px 20px rgba(59, 130, 246, 0.4);
      background: linear-gradient(135deg, #2563eb, #1d4ed8);
    }

    &:active {
      transform: translateY(0);
    }
  }
}
</style>
